import { useId, useMemo, useRef, useState, type CSSProperties } from 'react'
import { ChevronDown, Search, X } from 'lucide-react'
import {
  MAX_COMPETITORS,
  competitorsIn,
  isAtCompetitorCap,
  isOwn,
  matchesQuery,
  toggleBrand,
} from '../brandSelection'
import type { BrandOption } from '../landing'
import { brand, formatNumber } from '../theme'
import { usePopoverDismiss } from '../usePopoverDismiss'
import { InfoTip } from './InfoTip'
import { ownerLabel } from '../missingLabels'

// Searchable multi-select over every brand in the market (MP-OS-98). Replaces the
// hardcoded four-chip row and the fixed "Jämför Zyn · Velo" toggle: the customer happens to
// care about Zyn and Velo, the next manufacturer about Loop or Helwit. Own brands are
// listed first and marked; competitors are capped so the chart stays readable.

const RB = 'var(--radius-btn)'

const S: Record<string, CSSProperties> = {
  wrap: { position: 'relative', display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 5, border: `1px solid ${brand.grey}`,
    background: brand.surface, borderRadius: RB, padding: '5px 8px', fontSize: 12.5,
    fontWeight: 600, fontFamily: 'inherit', color: brand.ink,
  },
  chipOwn: { borderColor: brand.tealDark, color: brand.tealDark },
  chipX: { display: 'inline-flex', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'inherit', lineHeight: 1 },
  add: {
    border: `1px dashed ${brand.grey}`, background: 'transparent', borderRadius: RB,
    padding: '5px 10px', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
    color: brand.greyText, cursor: 'pointer',
  },
  select: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    border: `1px solid ${brand.grey}`, background: brand.surface, borderRadius: RB,
    padding: '6px 8px', fontSize: 12.5, fontFamily: 'inherit',
    color: brand.ink, cursor: 'pointer',
  },
  panel: {
    position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 30, width: 280,
    background: brand.surface, border: `1px solid ${brand.grey}`, borderRadius: 'var(--radius-card)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.10)', padding: 8,
  },
  searchRow: { display: 'flex', alignItems: 'center', gap: 6, borderBottom: `1px solid ${brand.grey}`, paddingBottom: 6, marginBottom: 6 },
  search: { flex: 1, border: 'none', outline: 'none', background: 'transparent', font: 'inherit', fontSize: 13, color: brand.ink },
  list: { maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 },
  opt: {
    display: 'flex', alignItems: 'baseline', gap: 8, width: '100%', border: 'none',
    background: 'transparent', borderRadius: RB, padding: '6px 8px', fontSize: 12.5,
    fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left', color: brand.ink,
  },
  optOn: { background: brand.mint, fontWeight: 600 },
  optDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  optMeta: { marginLeft: 'auto', fontSize: 11, color: brand.greyText, fontVariantNumeric: 'tabular-nums' },
  ownTag: { fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: brand.tealDark },
  groupLabel: { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: brand.greyText, padding: '6px 8px 2px' },
  hint: { fontSize: 11.5, color: brand.greyText, padding: '6px 8px 2px', borderTop: `1px solid ${brand.grey}`, marginTop: 4 },
}

export function BrandPicker({
  options,
  selection,
  ownBrands,
  onChange,
  max = MAX_COMPETITORS,
  compact = false,
}: {
  options: BrandOption[]
  selection: string[]
  ownBrands: readonly string[]
  onChange: (next: string[]) => void
  max?: number
  compact?: boolean
}) {
  const panelId = useId()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const wrapRef = useRef<HTMLSpanElement>(null)
  // Escape (from anywhere, including <body> after an option click disables the filters
  // fieldset) and focus return are the shared popover behaviour (MP-OS-188 bug #4).
  usePopoverDismiss({
    open,
    close: () => { setOpen(false); setQuery('') },
    triggerRef,
    containerRef: wrapRef,
  })

  // The server knows new brands and numeric manufacturer scopes that are not
  // present in the static tenant register. Ownership must agree with the own tag.
  const resolvedOwnBrands = useMemo(() => [...new Set([
    ...ownBrands, ...options.filter((o) => o.own).map((o) => o.brand),
  ])], [options, ownBrands])

  const atCap = isAtCompetitorCap(selection, resolvedOwnBrands, max)
  const nCompetitors = competitorsIn(selection, resolvedOwnBrands).length

  const { own, rest } = useMemo(() => {
    const hits = options.filter((o) => matchesQuery(o.brand, query))
    return {
      own: hits.filter((o) => isOwn(o.brand, resolvedOwnBrands)),
      rest: hits.filter((o) => !isOwn(o.brand, resolvedOwnBrands)),
    }
  }, [options, query, resolvedOwnBrands])

  const isSelected = (b: string) => selection.some((s) => s.toUpperCase() === b.toUpperCase())
  const pick = (b: string) => onChange(toggleBrand(selection, b, resolvedOwnBrands, max))

  const renderOption = (o: BrandOption) => {
    const on = isSelected(o.brand)
    const ownBrand = isOwn(o.brand, resolvedOwnBrands)
    const blocked = !on && !ownBrand && atCap
    return (
      <button
        key={o.brand}
        role="option"
        aria-selected={on}
        disabled={blocked}
        title={blocked ? `Max ${max} competitors at once — remove a brand first` : ownerLabel(o.producer)}
        style={{ ...S.opt, ...(on ? S.optOn : {}), ...(blocked ? S.optDisabled : {}) }}
        onClick={() => pick(o.brand)}
      >
        <span>{on ? '✓' : ' '}</span>
        <span>{o.brand}</span>
        {ownBrand && <span style={S.ownTag}>own</span>}
        <span style={S.optMeta}>{o.dosor > 0 ? formatNumber(o.dosor) : ''}</span>
      </button>
    )
  }

  return (
    <span ref={wrapRef} style={S.wrap}>
      {!compact && selection.map((b) => {
        const ownBrand = isOwn(b, resolvedOwnBrands)
        return (
          <span key={b} style={{ ...S.chip, ...(ownBrand ? S.chipOwn : {}) }}>
            {b}
            <button style={S.chipX} aria-label={`Remove ${b}`} onClick={() => pick(b)}>
              <X size={12} aria-hidden />
            </button>
          </span>
        )
      })}
      <button
        ref={triggerRef}
        style={compact ? S.select : S.add}
        aria-label={compact ? 'Filter by brand' : 'Add brand'}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          setOpen((v) => !v)
          setTimeout(() => inputRef.current?.focus(), 0)
        }}
      >
        {compact ? (selection.length === 0 ? 'All brands' : selection.length === 1 ? selection[0] : `${selection.length} brands`) : '+ Brand'}
        <ChevronDown size={12} aria-hidden />
      </button>

      {open && (
        <>
          {/* Click-away: a transparent full-screen layer beneath the panel. */}
          <span
            style={{ position: 'fixed', inset: 0, zIndex: 20 }}
            onClick={() => {
              setOpen(false)
              setQuery('')
            }}
          />
          <div id={panelId} style={S.panel}>
            <div style={S.searchRow}>
              <Search size={14} color={brand.greyText} aria-hidden />
              <input
                ref={inputRef}
                style={S.search}
                value={query}
                placeholder="Search brand…"
                aria-label="Search brand"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            {compact && (
              <button style={S.opt} disabled={selection.length === 0} onClick={() => onChange([])}>
                Clear brand selection
              </button>
            )}
            <div style={S.list} role="listbox" aria-label="Select brands" aria-multiselectable="true">
              {own.length > 0 && <span style={S.groupLabel}>Own brands</span>}
              {own.map(renderOption)}
              {rest.length > 0 && <span style={S.groupLabel}>The market</span>}
              {rest.map(renderOption)}
              {own.length === 0 && rest.length === 0 && (
                <span style={{ ...S.opt, color: brand.greyText }}>No brands match.</span>
              )}
            </div>
            <div style={S.hint}>
              {nCompetitors}/{max} competitors selected
              <InfoTip label="About the brand list" size="small">
                Your own brands do not count toward the limit. The volumes in this list are CU over all
                available history, not the chart period.
              </InfoTip>
            </div>
          </div>
        </>
      )}
    </span>
  )
}
